/**
 * Features related to testarossa
 * @module services/testarossa
 */
import request from 'services/request'
import { fromJS } from 'immutable'
import { createAuthorization } from '../auth'

/**
 * Get all of the testarossa projects with campaign uuids
 * @param {Object} options The options
 * @param {String} options.server The server uri
 * @param {import('immutable').Map} options.auth The auth information
 * @returns {Promise<import('immutable').List>} An immutable List of projects Maps
 */
export async function getProjects (options = {}) {
  const { auth, server } = options
  if (!server) {
    throw new Error('The server option is required')
  }
  if (!auth) {
    throw new Error('The auth option is required')
  }
  const { body = [] } = await request
    .get(`${server}/v1/projects`)
    .query({ noCache: true })
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return fromJS(body)
}

/**
 * Get one testarossa project with campaign details and variation uuids
 * @param {Object} options The options
 * @param {String} options.uuid The project uuid
 * @param {String} options.server The server uri
 * @param {import('immutable').Map} options.auth The auth information
 * @returns {Promise<import('immutable').Map>} The project as a Map
 */
export async function getProject (options = {}) {
  const { auth, server, uuid } = options
  if (!server) {
    throw new Error('The server option is required')
  }
  if (!auth) {
    throw new Error('The auth option is required')
  }
  if (!uuid) {
    throw new Error('The uuid option is required')
  }
  const { body = {} } = await request
    .get(`${server}/v1/projects/${uuid}`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return fromJS(body)
}

/**
 * Get one testarossa campaign with variation details
 * @param {Object} options The options
 * @param {String} options.uuid The campaign uuid
 * @param {String} options.server The server uri
 * @param {import('immutable').Map} options.auth The auth information
 * @returns {Promise<import('immutable').Map>} The campaign as a Map
 */
export async function getCampaign (options = {}) {
  const { auth, server, uuid } = options
  if (!server) {
    throw new Error('The server option is required')
  }
  if (!auth) {
    throw new Error('The auth option is required')
  }
  if (!uuid) {
    throw new Error('The uuid option is required')
  }
  const { body = {} } = await request
    .get(`${server}/v1/campaigns/${uuid}`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return fromJS(body)
}
